import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Check, CircleAlert, FileText, ShieldCheck } from 'lucide-react';

interface HeroSectionProps {
  onScrollToDemo: () => void;
}

const ledger = [
  { status: "APPROVAL READY", icon: Check, title: "Update Acme renewal opportunity", detail: "Stage, amount and next step sourced from today's call", destination: "Salesforce" },
  { status: "RISK DETECTED", icon: CircleAlert, title: "Champion went quiet", detail: "No buyer response in 12 days. Decision date is approaching.", destination: "Slack" },
  { status: "ACTION DRAFTED", icon: FileText, title: "Send technical follow-up", detail: "Owner, due date and supporting context prepared for review", destination: "HubSpot" },
];

export const HeroSection = ({ onScrollToDemo }: HeroSectionProps) => {
  return (
    <section className="w-full bg-background border-b border-foreground/20">
      <div className="container px-4 md:px-6 py-14 md:py-20 lg:py-24">
        <div className="grid lg:grid-cols-[minmax(0,0.92fr)_minmax(520px,1.08fr)] gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-6">AI Chief of Staff for Revenue</p>
            <h1 className="text-[2.55rem] sm:text-5xl md:text-6xl lg:text-[4.15rem] font-bold leading-[1.08] text-foreground mb-7">
              Your CRM should know what happened.
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed mb-8">
              NoQ5 turns calls, meetings and emails into approved CRM updates, deal risks and next actions. Revenue leaders get the truth without asking reps to become data clerks.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <Button size="lg" className="h-12 px-7 font-semibold group" onClick={onScrollToDemo}>
                Book a demo
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button size="lg" variant="outline" className="h-12 px-7 border-foreground/30 font-semibold" asChild>
                <a href="/product/platform">See the platform</a>
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> EU-hosted</span>
              <span>GDPR aligned</span>
              <a href="/crm-optional" className="font-semibold text-foreground hover:underline underline-offset-4">No CRM yet? Start here →</a>
            </div>
          </div>

          <div className="min-w-0 border border-foreground/30 bg-card shadow-card">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-foreground/20 px-4 sm:px-5 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Execution ledger</p>
                <p className="text-sm font-semibold text-foreground mt-1">Tuesday, 10:42</p>
              </div>
              <span className="text-xs font-medium border border-foreground/20 px-2.5 py-1 bg-background">3 items for review</span>
            </div>
            <div className="divide-y divide-foreground/15">
              {ledger.map((item) => (
                <div key={item.title} className="grid grid-cols-[32px_1fr] gap-3 sm:gap-4 p-4 sm:p-5">
                  <div className="w-8 h-8 border border-foreground/20 bg-background flex items-center justify-center">
                    <item.icon className="w-4 h-4 text-foreground" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-[10px] sm:text-xs font-semibold tracking-widest text-muted-foreground">{item.status}</p>
                      <span className="text-[10px] sm:text-xs text-muted-foreground shrink-0">{item.destination}</span>
                    </div>
                    <p className="font-semibold text-foreground mt-2">{item.title}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed mt-1">{item.detail}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-foreground/20 px-5 py-3 flex justify-between text-xs text-muted-foreground bg-muted/40">
              <span>Evidence attached to every recommendation</span>
              <span className="hidden sm:inline">Review before write-back</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
